/**
 * ManageFrameworksDialog
 *
 * Post-audit-creation framework lifecycle UI (Sprint B).
 *
 * Contract with the backend (`server.py`):
 *   - POST   /audits/{id}/frameworks                          {framework}   (async, returns operation_id)
 *   - GET    /audits/{id}/frameworks/operation/{op_id}        (poll)
 *   - DELETE /audits/{id}/frameworks/{framework}              {note?}
 *   - POST   /audits/{id}/frameworks/{framework}/reactivate
 *   - GET    /audits/{id}/frameworks/impact-preview?framework=...
 *
 * Design rules (locked with product owner):
 *   1. Retire = soft-delete only. Every retire is reversible via Reactivate.
 *   2. No notifications on any action. New controls appear as Unassigned.
 */
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Loader2, ShieldCheck, Plus, Trash2, Undo2, AlertTriangle } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// Frameworks the backend has a built-in prompt template for. Server also
// accepts any framework that has a custom row in `db.prompt_templates`, so
// this is a floor, not a ceiling.
const BUILTIN_FRAMEWORKS = ['SOC 2', 'GDPR', 'ISO 27001', 'PCI DSS', 'HIPAA'];

export default function ManageFrameworksDialog({
  open,
  onClose,
  audit,               // full audit object
  onChanged,           // async callback — parent refetches audit + controls
}) {
  const [tab, setTab] = useState('active');
  const [busyFw, setBusyFw] = useState(null);
  const [addingFw, setAddingFw] = useState(null);
  const [addProgress, setAddProgress] = useState(null); // {done, total, added, framework}
  const [confirmingRetire, setConfirmingRetire] = useState(null); // {framework, preview}
  const [retireNote, setRetireNote] = useState('');
  const [retireConfirm, setRetireConfirm] = useState('');
  const retireConfirmed = retireConfirm.trim().toLowerCase() === 'retire';

  const activeFws = audit?.frameworks || [];
  const retiredList = audit?.retired_frameworks || [];
  const retiredFwSet = new Set(retiredList.map(r => r.framework));

  const availableToAdd = useMemo(() => {
    return BUILTIN_FRAMEWORKS
      .filter(f => !activeFws.includes(f))
      .map(f => ({ framework: f, isRetiredHere: retiredFwSet.has(f) }));
  }, [activeFws, retiredFwSet]);

  const handleAdd = async (framework) => {
    setAddingFw(framework);
    setAddProgress({ done: 0, total: 0, added: 0, framework });
    try {
      const start = await axios.post(
        `${API}/audits/${audit.id}/frameworks`,
        { framework },
        { headers: authHeaders() },
      );
      const opId = start.data?.operation_id;
      if (!opId) {
        toast.error('Could not start add-framework operation');
        return;
      }
      // Poll every 2s until completed or failed. 15-min cap.
      const deadline = Date.now() + 15 * 60 * 1000;
      let final = null;
      while (Date.now() <= deadline) {
        await new Promise((r) => setTimeout(r, 2000));
        let st;
        try {
          st = await axios.get(
            `${API}/audits/${audit.id}/frameworks/operation/${opId}`,
            { headers: authHeaders() },
          );
        } catch (e) {
          // Transient network / Cloudflare hiccup — keep polling
          continue;
        }
        const d = st.data || {};
        setAddProgress({
          done: d.categories_done || 0,
          total: d.categories_total || 0,
          added: d.added || 0,
          framework: d.framework || framework,
        });
        if (d.status === 'completed' || d.status === 'failed') {
          final = d;
          break;
        }
      }
      if (!final && Date.now() > deadline) {
        toast.error('Add is taking longer than expected — check back later');
      }
      if (final?.status === 'completed') {
        toast.success(`Added ${final.framework} — ${final.added} new controls across ${final.categories_processed} categories.`);
        setTab('active');
      } else if (final?.status === 'failed') {
        toast.error(final.error || 'Add failed. Retry is safe — already-created controls will be skipped.');
      }
      await onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add framework');
    } finally {
      setAddingFw(null);
      setAddProgress(null);
    }
  };

  const openRetireConfirm = async (framework) => {
    setBusyFw(framework);
    try {
      const r = await axios.get(
        `${API}/audits/${audit.id}/frameworks/impact-preview`,
        { params: { framework }, headers: authHeaders() },
      );
      setConfirmingRetire({ framework, preview: r.data });
      setRetireNote('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not compute retire impact');
    } finally {
      setBusyFw(null);
    }
  };

  const handleRetireConfirm = async () => {
    if (!confirmingRetire) return;
    const fw = confirmingRetire.framework;
    setBusyFw(fw);
    try {
      const r = await axios.delete(
        `${API}/audits/${audit.id}/frameworks/${encodeURIComponent(fw)}`,
        { data: { note: retireNote || null }, headers: authHeaders() },
      );
      const soft = r.data?.controls_soft_deleted ?? 0;
      const demo = r.data?.controls_demoted ?? 0;
      const strip = r.data?.controls_applicable_stripped ?? 0;
      toast.success(`Retired ${fw} — ${soft} archived, ${demo} demoted, ${strip} un-mapped. Reversible from Retired tab.`);
      setConfirmingRetire(null);
      setRetireNote('');
      setRetireConfirm('');
      await onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to retire framework');
    } finally {
      setBusyFw(null);
    }
  };

  const handleReactivate = async (framework) => {
    setBusyFw(framework);
    try {
      const r = await axios.post(
        `${API}/audits/${audit.id}/frameworks/${encodeURIComponent(framework)}/reactivate`,
        {},
        { headers: authHeaders() },
      );
      const restored = r.data?.controls_restored ?? 0;
      const repro = r.data?.controls_re_promoted ?? 0;
      const remap = r.data?.controls_re_applicable ?? 0;
      toast.success(`Reactivated ${framework} — ${restored} restored, ${repro} re-promoted, ${remap} re-mapped.`);
      await onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reactivate framework');
    } finally {
      setBusyFw(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-3xl" data-testid="manage-frameworks-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-slate-700" />
            Manage Frameworks
          </DialogTitle>
          <DialogDescription>
            Add frameworks to generate new controls, retire the ones no longer required, or reactivate previously retired ones. Retire is always reversible — evidence is preserved.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="active" data-testid="manage-fw-tab-active">Active ({activeFws.length})</TabsTrigger>
            <TabsTrigger value="retired" data-testid="manage-fw-tab-retired">Retired ({retiredList.length})</TabsTrigger>
            <TabsTrigger value="add" data-testid="manage-fw-tab-add">Add</TabsTrigger>
          </TabsList>

          {/* --- Active tab --- */}
          <TabsContent value="active" className="mt-4">
            {activeFws.length === 0 && (
              <div className="text-sm text-slate-500 py-8 text-center">No frameworks attached to this audit yet.</div>
            )}
            <ul className="divide-y divide-slate-200 rounded-md border border-slate-200 bg-white">
              {activeFws.map((fw) => (
                <li key={fw} className="flex items-center justify-between px-4 py-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{fw}</div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openRetireConfirm(fw)}
                    disabled={busyFw === fw}
                    data-testid={`retire-fw-${fw}`}
                  >
                    {busyFw === fw ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1 text-rose-600" />}
                    Retire
                  </Button>
                </li>
              ))}
            </ul>
          </TabsContent>

          {/* --- Retired tab --- */}
          <TabsContent value="retired" className="mt-4">
            {retiredList.length === 0 && (
              <div className="text-sm text-slate-500 py-8 text-center">No retired frameworks. Retire an active one and it will show up here for one-click undo.</div>
            )}
            <ul className="divide-y divide-slate-200 rounded-md border border-slate-200 bg-white">
              {retiredList.map((r) => (
                <li key={r.framework} className="px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">
                        {r.framework}
                        <Badge variant="secondary" className="ml-2 text-[10px] uppercase tracking-wide">Retired</Badge>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {r.retired_at ? new Date(r.retired_at).toLocaleString() : ''} · {r.retired_by_email || 'unknown'} ·{' '}
                        {(r.soft_deleted_control_ids?.length || 0)} archived / {(r.demoted_control_ids?.length || 0)} demoted / {(r.applicable_stripped_control_ids?.length || 0)} un-mapped
                      </div>
                      {r.note && <div className="text-xs text-slate-600 italic mt-1">“{r.note}”</div>}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleReactivate(r.framework)}
                      disabled={busyFw === r.framework}
                      data-testid={`reactivate-fw-${r.framework}`}
                    >
                      {busyFw === r.framework ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Undo2 className="w-3.5 h-3.5 mr-1" />}
                      Reactivate
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </TabsContent>

          {/* --- Add tab --- */}
          <TabsContent value="add" className="mt-4">
            {addingFw && addProgress && (
              <div
                className="mb-4 rounded-md border border-slate-200 bg-slate-50 px-4 py-3"
                data-testid="add-fw-progress"
              >
                <div className="flex items-center justify-between text-sm text-slate-800">
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-slate-600" />
                    Generating controls
                    {addProgress.framework ? <span className="font-medium"> · {addProgress.framework}</span> : null}
                  </span>
                  <span className="text-xs text-slate-500">
                    {addProgress.total > 0
                      ? `${addProgress.done}/${addProgress.total} categories`
                      : 'Planning…'}
                  </span>
                </div>
                <div className="mt-2 h-1.5 w-full bg-slate-200 rounded overflow-hidden">
                  <div
                    className="h-full bg-slate-800 transition-all"
                    style={{
                      width: addProgress.total > 0
                        ? `${Math.min(100, (addProgress.done / addProgress.total) * 100)}%`
                        : '10%',
                    }}
                  />
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {addProgress.added} control{addProgress.added === 1 ? '' : 's'} created so far · safe to leave this open
                </div>
              </div>
            )}
            {availableToAdd.length === 0 && (
              <div className="text-sm text-slate-500 py-8 text-center">
                All supported frameworks are already attached to this audit.
              </div>
            )}
            <ul className="divide-y divide-slate-200 rounded-md border border-slate-200 bg-white">
              {availableToAdd.map((f) => (
                <li key={f.framework} className="flex items-center justify-between px-4 py-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">
                      {f.framework}
                      {f.isRetiredHere && (
                        <Badge variant="secondary" className="ml-2 text-[10px] uppercase tracking-wide">Previously retired</Badge>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleAdd(f.framework)}
                    disabled={addingFw != null}
                    data-testid={`add-fw-${f.framework}`}
                  >
                    {addingFw === f.framework ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
                    {addingFw === f.framework ? 'Generating…' : (f.isRetiredHere ? 'Reactivate & regenerate' : 'Add')}
                  </Button>
                </li>
              ))}
            </ul>
            <p className="text-xs text-slate-500 mt-2">
              Adding a framework runs AI generation for every category in the background — safe to close and check back. Retries are safe: already-created controls are skipped. Owners are not auto-notified.
            </p>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} data-testid="manage-fw-close">Close</Button>
        </DialogFooter>

        {/* --- Retire confirmation nested dialog --- */}
        <Dialog open={!!confirmingRetire} onOpenChange={(v) => { if (!v) { setConfirmingRetire(null); setRetireConfirm(''); } }}>
          <DialogContent className="max-w-md" data-testid="retire-fw-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" /> Retire framework?
              </DialogTitle>
              <DialogDescription>
                This is reversible from the Retired tab. Evidence, comments, drafts and owner assignments are preserved.
              </DialogDescription>
            </DialogHeader>
            {confirmingRetire && (
              <div className="text-sm space-y-2">
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-1">
                  <div><strong>{confirmingRetire.preview?.controls_soft_deleted ?? 0}</strong> control(s) will be moved to <em>Deleted Controls</em> (archived).</div>
                  <div><strong>{confirmingRetire.preview?.controls_demoted ?? 0}</strong> multi-framework control(s) will be demoted to their next remaining framework.</div>
                  <div><strong>{confirmingRetire.preview?.controls_applicable_stripped ?? 0}</strong> cross-mapped control(s) will just drop this framework from their applicable list.</div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wide text-slate-500 mb-1 block">Reason (optional)</label>
                  <Textarea
                    rows={2}
                    value={retireNote}
                    onChange={(e) => setRetireNote(e.target.value)}
                    placeholder="e.g. Descoped SOC 2 for FY2026"
                    data-testid="retire-fw-note"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wide text-slate-500 mb-1 block">
                    Type <span className="font-semibold text-rose-600">retire</span> to confirm
                  </label>
                  <Input
                    value={retireConfirm}
                    onChange={(e) => setRetireConfirm(e.target.value)}
                    placeholder="retire"
                    autoComplete="off"
                    data-testid="retire-fw-confirm-input"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setConfirmingRetire(null); setRetireConfirm(''); }} data-testid="retire-fw-cancel">Cancel</Button>
              <Button
                onClick={handleRetireConfirm}
                disabled={busyFw === confirmingRetire?.framework || !retireConfirmed}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="retire-fw-confirm"
              >
                {busyFw === confirmingRetire?.framework ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
                Retire framework
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
